/**
 * Modal content normalization and helpers.
 */
import { __ } from '@wordpress/i18n';
import {
	decodeHtmlEntities,
	escapeHtml,
} from '../shared/html-entities';
import {
	MODAL_TYPES,
	WEBSITE_VARIANTS,
	FUND_MODAL_CONVENTIONAL,
	FUND_MODAL_SHARIAH,
} from './constants';
import {
	FUND_LAYOUT_VARIANTS,
	CAPITAL_MODAL_DEFAULTS,
	getCapitalModalDefaults,
} from './modal-constants';

/**
 * Resolve a hardcoded modal chrome label.
 *
 * Prefer PHP/WPML-localized values from `alteiaMtmModalI18n` on the frontend.
 * Fall back to gettext so the editor and non-WPML installs still work.
 *
 * @param {string} key      CamelCase key from alteiaMtmModalI18n.
 * @param {string} fallback English gettext fallback.
 * @return {string} Translated label.
 */
export function getModalChromeLabel( key, fallback ) {
	const labels =
		typeof window !== 'undefined' && window.alteiaMtmModalI18n
			? window.alteiaMtmModalI18n
			: null;

	if ( labels && typeof labels[ key ] === 'string' && labels[ key ] ) {
		return labels[ key ];
	}

	return fallback;
}

/**
 * Resolve the modal type from the section website variant.
 *
 * The modal design is always driven by the section variant, so cards do not
 * need an independent modal-type control.
 *
 * @param {string} websiteVariant Section website variant.
 * @return {string} Modal type.
 */
export function getModalTypeForVariant( websiteVariant ) {
	return websiteVariant === WEBSITE_VARIANTS.CAPITAL
		? MODAL_TYPES.CAPITAL
		: MODAL_TYPES.FUND;
}

export { decodeHtmlEntities, escapeHtml };

/**
 * Recursively decode HTML entities in modal strings.
 *
 * @param {*} value Modal value.
 * @return {*} Decoded value.
 */
export function decodeModalValue( value ) {
	if ( typeof value === 'string' ) {
		return decodeHtmlEntities( value );
	}

	if ( Array.isArray( value ) ) {
		return value.map( decodeModalValue );
	}

	if ( value && typeof value === 'object' ) {
		return Object.fromEntries(
			Object.entries( value ).map( ( [ key, item ] ) => [
				key,
				decodeModalValue( item ),
			] )
		);
	}

	return value;
}

const RICH_TEXT_ALLOWED_TAGS = new Set( [
	'P',
	'STRONG',
	'EM',
	'B',
	'I',
	'A',
	'UL',
	'OL',
	'LI',
	'BR',
	'H3',
	'H4',
	'H5',
	'TABLE',
	'THEAD',
	'TBODY',
	'TR',
	'TH',
	'TD',
	'SPAN',
] );

/**
 * Sanitize trusted editor HTML for modal output.
 *
 * @param {string} value Raw HTML or plain text.
 * @return {string} Safe HTML string.
 */
export function sanitizeRichHtml( value ) {
	const stringValue = String( value || '' ).trim();

	if ( ! stringValue ) {
		return '';
	}

	const decodedValue = decodeHtmlEntities( stringValue );

	if ( ! /[<>]/.test( decodedValue ) ) {
		return escapeHtml( decodedValue );
	}

	if ( typeof DOMParser === 'undefined' ) {
		return escapeHtml( decodedValue );
	}

	const doc = new DOMParser().parseFromString( decodedValue, 'text/html' );

	const sanitizeNode = ( node ) => {
		Array.from( node.children ).forEach( ( child ) => {
			if ( ! RICH_TEXT_ALLOWED_TAGS.has( child.tagName ) ) {
				while ( child.firstChild ) {
					child.parentNode.insertBefore( child.firstChild, child );
				}
				child.remove();
				sanitizeNode( node );
				return;
			}

			Array.from( child.attributes ).forEach( ( attribute ) => {
				const name = attribute.name.toLowerCase();

				if ( child.tagName === 'A' && name === 'href' ) {
					return;
				}

				if ( child.tagName === 'TH' && name === 'scope' ) {
					return;
				}

				child.removeAttribute( attribute.name );
			} );

			if ( child.tagName === 'A' ) {
				const href = child.getAttribute( 'href' ) || '';

				if ( ! href || /^javascript:/i.test( href ) ) {
					child.removeAttribute( 'href' );
				} else if ( child.getAttribute( 'target' ) === '_blank' ) {
					child.setAttribute( 'rel', 'noopener noreferrer' );
				}
			}

			sanitizeNode( child );
		} );
	};

	sanitizeNode( doc.body );

	return doc.body.innerHTML;
}

const HTML_HOW_IT_WORKS_CONVENTIONAL =
	'<p>Capital is deployed into carefully selected trade transactions involving commodities and other physical goods.</p><ul><li>Identifiable underlying assets</li><li>First-ranking security</li><li>Legal documentation completed before funds are released</li></ul>';

const HTML_HOW_IT_WORKS_SHARIAH =
	'<p>Transactions are based on the purchase and sale of physical commodities in accordance with Shariah principles.</p><ul><li>Commodity ownership forms part of the transaction</li><li>Capital is deployed only after the underlying commodity is identified and priced</li><li>Transactions are reviewed for Shariah compliance</li></ul>';

/**
 * Previous Fund modal how-it-works copy. Used to upgrade unsaved defaults
 * without overwriting editor customizations.
 */
const LEGACY_FUND_MODAL_CONTENT = {
	'Capital is deployed into trade transactions involving commodities and other physical goods. Each facility is backed by tangible underlying assets, with security confirmed and legal documentation finalised before funds are released.':
		FUND_MODAL_CONVENTIONAL,
	'Transactions are structured around physical commodities, with ownership transferred in accordance with Shariah principles. Capital deployment, profit distribution, and risk management follow defined Shariah-compliant processes.':
		FUND_MODAL_SHARIAH,
	[ HTML_HOW_IT_WORKS_CONVENTIONAL ]: FUND_MODAL_CONVENTIONAL,
	[ HTML_HOW_IT_WORKS_SHARIAH ]: FUND_MODAL_SHARIAH,
};

/**
 * Decode common HTML entities so stored markup can be parsed.
 *
 * @param {string} value Raw value.
 * @return {string} Decoded string.
 */
function decodeBasicEntities( value ) {
	return decodeHtmlEntities( value );
}

/**
 * Split How it works HTML into intro text and bullet items.
 *
 * @param {string} howItWorks      Intro copy or legacy HTML string.
 * @param {Array}  howItWorksItems Existing bullet items.
 * @return {Object} Parsed intro and items.
 */
function parseHowItWorksContent( howItWorks, howItWorksItems ) {
	const existingItems = Array.isArray( howItWorksItems )
		? howItWorksItems.filter( Boolean )
		: [];
	const intro = decodeBasicEntities( howItWorks ).trim();

	if ( ! /<[a-z][\s\S]*>/i.test( intro ) ) {
		return {
			howItWorks: intro,
			howItWorksItems: existingItems,
		};
	}

	if ( typeof DOMParser !== 'undefined' ) {
		const doc = new DOMParser().parseFromString( intro, 'text/html' );
		const parsedItems = Array.from( doc.querySelectorAll( 'li' ) )
			.map( ( item ) => item.textContent.trim() )
			.filter( Boolean );
		const paragraphs = Array.from( doc.querySelectorAll( 'p' ) )
			.map( ( paragraph ) => paragraph.textContent.trim() )
			.filter( Boolean );

		return {
			howItWorks: paragraphs.join( ' ' ),
			howItWorksItems: existingItems.length ? existingItems : parsedItems,
		};
	}

	const listMatches = [ ...intro.matchAll( /<li[^>]*>([\s\S]*?)<\/li>/gi ) ];
	const parsedItems = listMatches
		.map( ( match ) => match[ 1 ].replace( /<[^>]+>/g, '' ).trim() )
		.filter( Boolean );
	const paragraphMatch = intro.match( /<p[^>]*>([\s\S]*?)<\/p>/i );

	return {
		howItWorks: paragraphMatch
			? paragraphMatch[ 1 ].replace( /<[^>]+>/g, '' ).trim()
			: intro
					.replace( /<[^>]+>/g, ' ' )
					.replace( /\s+/g, ' ' )
					.trim(),
		howItWorksItems: existingItems.length ? existingItems : parsedItems,
	};
}

/**
 * Replace previous Fund default copy with the redesigned content.
 *
 * @param {Object} modalContent Stored modal content.
 * @return {Object} Upgraded content when the previous default is detected.
 */
export function upgradeLegacyFundModalContent( modalContent = {} ) {
	const howItWorks = decodeBasicEntities( modalContent.howItWorks || '' );

	return (
		LEGACY_FUND_MODAL_CONTENT[ howItWorks ] ||
		LEGACY_FUND_MODAL_CONTENT[ modalContent.howItWorks ] ||
		modalContent
	);
}

/**
 * Normalize legacy fund modal content into block objects.
 *
 * @param {Object} modalContent Modal content attribute.
 * @return {Object} Normalized fund blocks.
 */
export function normalizeFundModalContent( modalContent = {} ) {
	const content = decodeModalValue(
		upgradeLegacyFundModalContent( modalContent )
	);
	const howItWorks = parseHowItWorksContent(
		content.howItWorks,
		content.howItWorksItems
	);

	return {
		overview: content.overview || '',
		howItWorks: howItWorks.howItWorks,
		howItWorksItems: howItWorks.howItWorksItems,
		availableVehicles: content.availableVehicles || '',
		keyCharacteristics: Array.isArray( content.keyCharacteristics )
			? content.keyCharacteristics.filter( Boolean )
			: [],
		keyCharacteristicsFootnote: content.keyCharacteristicsFootnote || '',
		nextStep: content.nextStep || '',
		ctaLabel:
			content.ctaLabel ||
			getModalChromeLabel(
				'getInTouch',
				__( 'Get in touch', 'alteia' )
			),
		ctaUrl: content.ctaUrl || '',
		ctaOpensInNewTab: content.ctaOpensInNewTab !== false,
	};
}

const LEGACY_CAPITAL_ABOUT = {
	'The Alteia Saudi Murabaha fund provides investors with access to short-duration Murabaha financing opportunities structured in accordance with Shariah principles. The fund is managed by Alteia Capital, a Saudi CMA-licensed investment firm, with independent Shariah oversight provided by the Shariah Review Bureau.':
		'alteia-saudi-murabaha',
	'The Alteia Saudi Musharaka fund provides investors with access to partnership-based commercial opportunities structured in accordance with Shariah principles. Profits and losses are shared between partners in accordance with Musharaka arrangements, with independent Shariah oversight throughout.':
		'alteia-saudi-musharaka',
	'The Alfowzan Murabaha fund provides investors with access to Murabaha financing opportunities through a strategic partnership with Alfowzan Holding. The fund is structured in accordance with Shariah principles and managed by Alteia Capital.':
		'alfowzan-murabaha',
};

/**
 * Replace previous Capital default copy with the redesigned content.
 *
 * @param {Object} modalContent Stored modal content.
 * @return {Object} Upgraded content when the previous default is detected.
 */
export function upgradeLegacyCapitalModalContent( modalContent = {} ) {
	const contentId = LEGACY_CAPITAL_ABOUT[ modalContent.aboutTheFund ];

	if ( contentId && CAPITAL_MODAL_DEFAULTS[ contentId ] ) {
		return { ...CAPITAL_MODAL_DEFAULTS[ contentId ] };
	}

	return modalContent;
}

/**
 * Merge capital modal content with defaults.
 *
 * @param {Object} modalContent   Stored modal content.
 * @param {string} modalContentId Modal content identifier.
 * @return {Object} Normalized capital content.
 */
export function normalizeCapitalModalContent(
	modalContent = {},
	modalContentId = ''
) {
	const source = decodeModalValue(
		upgradeLegacyCapitalModalContent( modalContent )
	);
	const defaults = getCapitalModalDefaults( modalContentId );

	return {
		aboutTheFund: source.aboutTheFund ?? defaults.aboutTheFund,
		investmentObjective:
			source.investmentObjective ?? defaults.investmentObjective,
		fundOverview:
			source.fundOverview?.length > 0
				? source.fundOverview
				: defaults.fundOverview,
		investmentCharacteristics:
			source.investmentCharacteristics?.length > 0
				? source.investmentCharacteristics
				: defaults.investmentCharacteristics,
		fundDocuments:
			source.fundDocuments?.length > 0
				? source.fundDocuments
				: defaults.fundDocuments,
		interestedHeading:
			source.interestedHeading ?? defaults.interestedHeading,
		interestedText: source.interestedText ?? defaults.interestedText,
		ctaLabel: source.ctaLabel ?? defaults.ctaLabel,
		ctaUrl: source.ctaUrl ?? defaults.ctaUrl,
		ctaOpensInNewTab: source.ctaOpensInNewTab !== false,
	};
}

/**
 * Resolve fund layout variant with fallback.
 *
 * @param {string} layoutVariant  Layout variant value.
 * @param {string} modalContentId Modal content id for smart defaults.
 * @return {string} Resolved layout variant.
 */
export function resolveFundLayoutVariant( layoutVariant, modalContentId ) {
	if (
		layoutVariant === FUND_LAYOUT_VARIANTS.FULL_WIDTH_CTA ||
		layoutVariant === FUND_LAYOUT_VARIANTS.SPLIT_CTA
	) {
		return layoutVariant;
	}

	if ( modalContentId === 'shariah' ) {
		return FUND_LAYOUT_VARIANTS.SPLIT_CTA;
	}

	return FUND_LAYOUT_VARIANTS.FULL_WIDTH_CTA;
}

/**
 * Build modal grid class list.
 *
 * @param {Object} card Card attributes.
 * @return {string} Class string.
 */
export function getModalGridClassName( card ) {
	const modalType = card?.modalType || MODAL_TYPES.FUND;
	const classes = [
		'alteia-mtm-modal__grid',
		`alteia-mtm-modal__grid--${ modalType }`,
	];

	if ( modalType === MODAL_TYPES.FUND ) {
		const layout = resolveFundLayoutVariant(
			card?.modalLayoutVariant,
			card?.modalContentId
		);
		classes.push( `alteia-mtm-modal__grid--fund-${ layout }` );
	} else {
		classes.push( 'alteia-mtm-modal__grid--capital-standard' );
	}

	return classes.join( ' ' );
}

/**
 * Build unique modal element ids for a block instance.
 *
 * @param {string} blockInstanceId Block instance id.
 * @param {string} suffix          Element suffix.
 * @return {string} Unique id.
 */
export function getModalElementId( blockInstanceId, suffix ) {
	const safeId = String( blockInstanceId || 'block' ).replace(
		/[^a-zA-Z0-9_-]/g,
		''
	);
	return `alteia-mtm-modal-${ safeId }-${ suffix }`;
}

/**
 * Build modal root id.
 *
 * @param {string} blockInstanceId Block instance id.
 * @return {string} Modal root id.
 */
export function getModalRootId( blockInstanceId ) {
	return getModalElementId( blockInstanceId, 'root' );
}

/**
 * Prepare serializable modal payload for frontend script.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} Modal data payload.
 */
export function buildModalSectionData( attributes ) {
	const { blockInstanceId, websiteVariant, cards = [] } = attributes;
	const modalType = getModalTypeForVariant( websiteVariant );

	return {
		blockInstanceId: blockInstanceId || '',
		websiteVariant: websiteVariant || 'fund',
		cards: cards.map( ( card ) => ( {
			id: card.id || '',
			modalEnabled: card.modalEnabled !== false,
			modalTitle: decodeHtmlEntities(
				card.modalTitle || card.title || ''
			),
			modalType,
			modalLayoutVariant: resolveFundLayoutVariant(
				card.modalLayoutVariant,
				card.modalContentId
			),
			modalContentId: card.modalContentId || '',
			modalAccentColor: card.modalAccentColor || card.accentColor || '',
			modalContent:
				modalType === MODAL_TYPES.CAPITAL
					? normalizeCapitalModalContent(
							card.modalContent,
							card.modalContentId
					  )
					: normalizeFundModalContent( card.modalContent ),
		} ) ),
	};
}

/**
 * Find card modal data by id.
 *
 * @param {Object} sectionData Section modal data.
 * @param {string} cardId      Card id.
 * @return {Object|null} Card modal data.
 */
export function findCardModalData( sectionData, cardId ) {
	if ( ! sectionData?.cards || ! cardId ) {
		return null;
	}

	return sectionData.cards.find( ( card ) => card.id === cardId ) || null;
}
